import type { Row } from '@tanstack/react-table';

import type { Importer, IRejectionBreakdownLine } from '@feathr/blackbox';
import { getImportedRowCount } from '@feathr/extender/components/ImportRunLines/ImportRunLines.utils';

/**
 * Whether a row expands to the run-details panel. Every draft, pending and failed import does, and
 * the panel says what state it is in. A complete import does once the worker stamped
 * `date_started` (Feathr/anhinga#8511): one the old importer ran has no stamp, and keeps today's
 * row (Feathr/anhinga#8646 gates the screens on the record, not only on the account's flags).
 */
export function hasRunDetails(importer: Importer): boolean {
  if (importer.get('state') === 'complete') {
    return Boolean(importer.get('date_started'));
  }
  return true;
}

/**
 * Whether a worker has claimed the run and stamped `date_started`. A draft's stamp, and a pending
 * import's when it is not running, is the last run's.
 */
export function hasRunStarted(importer: Importer): boolean {
  const state = importer.get('state');
  if (state === 'draft' || (state === 'pending' && !importer.get('is_running'))) {
    return false;
  }
  return Boolean(importer.get('date_started'));
}

/**
 * How many rows a fully imported run wrote: a complete import that skipped none. The count is the
 * people it created plus the people it updated. `undefined` for any other import, including a
 * complete one that wrote nothing.
 */
export function getFullyImportedRowCount(importer: Importer): number | undefined {
  if (importer.get('state') !== 'complete' || importer.get('skipped', 0) > 0) {
    return undefined;
  }
  const count = getImportedRowCount(importer);
  return count > 0 ? count : undefined;
}

/** `TanStackTable`'s `getRowCanExpand`, at module scope so its identity is stable. */
export function canExpandImportRow(row: Row<Importer>): boolean {
  return hasRunDetails(row.original);
}

/** The rejection breakdown lines worth showing, most rows first. */
export function getRejectionLines(importer: Importer): IRejectionBreakdownLine[] {
  return (importer.get('rejection_breakdown') ?? [])
    .filter((line) => line.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** Whether a complete import wrote no rows. */
export function hasNothingImported(importer: Importer): boolean {
  return importer.get('state') === 'complete' && getImportedRowCount(importer) === 0;
}

/**
 * Whether a complete contact import created no group. A missing `segment` alone is not enough: it
 * is also missing once the group is deleted, and before the server attaches a new import's group.
 */
export function hasNoGroup(importer: Importer): boolean {
  return (
    importer.get('state') === 'complete' &&
    importer.get('kind') !== 'partner' &&
    !importer.get('segment') &&
    getImportedRowCount(importer) === 0
  );
}
